"use client";

import { createContext, useContext, useState, useCallback } from "react";
import { Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/outline";

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("success");

  const triggerToast = useCallback((msg, desc = "", type = "success") => {
    setMessage(msg);
    setDescription(desc);
    setType(type);
    setShow(true);
    setTimeout(() => setShow(false), 3000);
  }, []);

  const getColor = () => {
    switch (type) {
      case "error":
        return "text-red-500";
      case "warning":
        return "text-yellow-500";
      case "success":
      default:
        return "text-green-500";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "error":
        return <XCircleIcon className="w-6 h-6 text-red-500" />;
      case "warning":
        return <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500" />;
      case "success":
      default:
        return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
    }
  };

  return (
    <ToastContext.Provider value={{ triggerToast }}>
      {children}
      <div
        aria-live="assertive"
        className="fixed top-0 right-0 z-[9999] mt-4 mr-4 flex max-w-full flex-col items-end space-y-4 sm:mr-6 sm:mt-6"
      >
        <Transition
          show={show}
          enter="transform transition ease-out duration-300"
          enterFrom="translate-y-2 opacity-0"
          enterTo="translate-y-0 opacity-100"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="w-full max-w-xl bg-white rounded-lg shadow-lg border border-gray-200">
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">{getIcon()}</div>
                <div className="ml-3 w-full flex-1 pt-0.5">
                  <p className={`text-sm font-medium ${getColor()}`}>
                    {message}
                  </p>
                  {description && (
                    <p className="mt-1 text-sm text-gray-500">{description}</p>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={() => setShow(false)}
                    className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none bg-white rounded-md"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </ToastContext.Provider>
  );
};
